import { UploadOutlined } from "@ant-design/icons";
import { Divider, Modal, Upload } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import i18n from "i18next";
import type { FC } from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { getAssignedDataGroup } from "../../core/router/auth";
import { ResourceName } from "../../core/router/config/ResourceName";
import { baseAuthApi } from "../../core/service/baseApi";
import { getBeBaseUrl } from "../../core/setup/config/utils";
import type { ID } from "../../core/types/fe/types";
import { PrimaryButton, SecondaryButton } from "../Buttons/components/ActionButton";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { useUpdateTableDataMutation } from "../Table";
import {
    ImportErrorCountText,
    ImportErrorList,
    ImportErrorTitle,
    ImportInfoText,
    ImportResultContainer,
    ImportSuccessTitle,
    uploadPropsUtils,
} from "./csvModalButtonUtils";
import type { ImportResult } from "./types";

export type CSVImportButtonProps = {
    translationKey?: string;
    resourceName: string;
    action?: string;
    isDisabled: boolean;
    callback?: (file: File) => void;
    onSuccess?: () => void;
    maxRows?: number;
    id?: ID;
    format?: string;
    warningMessage?: string;
};

export const CSVImportButton: FC<CSVImportButtonProps> = ({
    translationKey,
    resourceName,
    action,
    isDisabled,
    callback,
    onSuccess,
    maxRows = 1000,
    id,
    format = "csv",
    warningMessage,
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isModalVisible2, setIsModalVisible2] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const dispatch = useDispatch();

    const showModal = () => {
        setIsModalVisible(true);
        setImportResult(null);
    };

    const handleClose = () => {
        setIsModalVisible(false);
        setFileList([]);
        setImportResult(null);
        setShowAllErrors(false);
    };

    const handleClose2 = () => {
        setIsModalVisible2(false);
    };

    const [importDataRequest] = useUpdateTableDataMutation();

    const handleOkClick = () => {
        if (warningMessage) setIsModalVisible2(true);
        else handleImport();
    };

    const handleImport = async () => {
        setIsModalVisible2(false);

        if (fileList.length === 0) {
            return;
        }

        const file = fileList[0].originFileObj as File;

        if (callback) {
            callback(file);
            handleClose();
            return;
        }

        setUploading(true);
        setImportResult(null);

        const dataGroup = getAssignedDataGroup();

        const idPath = id ? `/${id}` : "";
        const baseUrl = getBeBaseUrl();
        const url = `${baseUrl}${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${resourceName}${idPath}/import`;

        const formData = new FormData();
        formData.append("importFile", file);
        formData.append("format", format || "csv");
        formData.append("action", action || "add");

        importDataRequest({
            url,
            method: "POST",
            body: formData,
        })
            .unwrap()
            .then((result) => {
                // @ts-ignore
                setImportResult(result);
                if (onSuccess) {
                    onSuccess();
                } else {
                    dispatch(baseAuthApi.util.invalidateTags([resourceName]));
                }
            })
            .catch((error) => {
                setImportResult({
                    importErrors: [error?.data?.message || "batchUpdateData.modal.importError"],
                });
            })
            .finally(() => setUploading(false));
    };

    const renderImportResult = () => {
        if (!importResult) return null;

        const hasErrors = importResult.importErrors && importResult.importErrors.length > 0;

        return (
            <ImportResultContainer hasErrors={!!hasErrors}>
                {hasErrors && <ImportErrorTitle>{i18n.t("csvImport.modal.errors", { type: format?.toUpperCase() })}</ImportErrorTitle>}
                {!hasErrors && (
                    <ImportSuccessTitle>{i18n.t("csvImport.modal.successful", { type: format?.toUpperCase() })}</ImportSuccessTitle>
                )}

                {!hasErrors && typeof importResult.loadSuccessCount === "number" && importResult.loadSuccessCount > 0 && (
                    <ImportInfoText>
                        {i18n.t("csvImport.modal.recordsUpdated")} {importResult.loadSuccessCount}
                    </ImportInfoText>
                )}
                {!hasErrors && typeof importResult.importSuccessCount === "number" && importResult.importSuccessCount > 0 && (
                    <ImportInfoText>
                        {i18n.t("csvImport.modal.recordsCreated")} {importResult.importSuccessCount}
                    </ImportInfoText>
                )}

                {typeof importResult.loadFailedCount === "number" && importResult.loadFailedCount > 0 && (
                    <ImportInfoText>
                        {i18n.t("csvImport.modal.recordsDeleted")} {importResult.loadFailedCount}
                    </ImportInfoText>
                )}
                {typeof importResult.importFailedCount === "number" && importResult.importFailedCount > 0 && (
                    <ImportErrorCountText>
                        {i18n.t("csvImport.modal.numberOfErrors")} {importResult.importFailedCount}
                    </ImportErrorCountText>
                )}
                {hasErrors && (
                    <>
                        <Divider />
                        <ImportErrorTitle>{i18n.t("csvImport.modal.incorrectLines", { type: format?.toUpperCase() })}</ImportErrorTitle>
                        <ImportErrorList>
                            {importResult.importErrors?.slice(0, showAllErrors ? undefined : 5).map((error) => (
                                <li key={error}>{i18n.t(error)}</li>
                            ))}
                        </ImportErrorList>
                        {importResult.importErrors && importResult.importErrors.length > 5 && (
                            <SecondaryButton
                                onClick={() => setShowAllErrors(!showAllErrors)}
                                style={{ marginTop: 8, fontSize: "12px", height: "28px" }}
                            >
                                {showAllErrors
                                    ? i18n.t("csvImport.modal.showLess")
                                    : i18n.t("csvImport.modal.showMore", {
                                          "0": String(importResult.importErrors.length - 5),
                                      })}
                            </SecondaryButton>
                        )}
                    </>
                )}
            </ImportResultContainer>
        );
    };

    const hasBeenSuccessfulImport = !!importResult && (!importResult.importErrors || importResult.importErrors.length === 0);

    // Limit file size to 10 MB for imports
    const optionalFileSizeLimit = 10 * 1024 * 1024;
    const uploadProps = uploadPropsUtils(fileList, setFileList, { maxSizeBytes: optionalFileSizeLimit }, setImportResult);

    return (
        <>
            <SecondaryButton name="csv_import_button" onClick={showModal} disabled={isDisabled}>
                {i18n.t(translationKey ?? "button.importCsv")}
            </SecondaryButton>

            {warningMessage && (
                <Modal
                    title={i18n.t("core.notifications.warning.title")}
                    open={isModalVisible2}
                    onCancel={handleClose2}
                    footer={[
                        <SecondaryButton key="close" onClick={handleClose2}>
                            {i18n.t("common.close")}
                        </SecondaryButton>,
                        <PrimaryButton key="import" onClick={handleImport}>
                            {i18n.t("form.ok")}
                        </PrimaryButton>,
                    ]}
                >
                    <p>{i18n.t(warningMessage)}</p>
                </Modal>
            )}

            <Modal
                title={
                    translationKey
                        ? `${i18n.t("csvImport.modal.title", { type: format?.toUpperCase() })} - ${i18n.t(translationKey)}`
                        : i18n.t("csvImport.modal.title", { type: format?.toUpperCase() })
                }
                open={isModalVisible}
                onCancel={handleClose}
                footer={[
                    <SecondaryButton key="close" onClick={handleClose}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    <PrimaryButton
                        key="import"
                        onClick={handleOkClick}
                        disabled={fileList.length === 0 || uploading || hasBeenSuccessfulImport}
                    >
                        {i18n.t("button.import")}
                    </PrimaryButton>,
                ]}
            >
                <p>{i18n.t("csvImport.modal.maximumRecords", { "0": String(maxRows), type: format?.toUpperCase() })}</p>
                <Upload {...uploadProps}>
                    <SecondaryButton icon={<UploadOutlined />}>{i18n.t("csvImport.modal.addFile")}</SecondaryButton>
                </Upload>
                {uploading && <ProgressBar />}
                {renderImportResult()}
            </Modal>
        </>
    );
};
